import { useState } from 'react';
import { Group, Stack, Text, Textarea } from '@mantine/core';
import { BookOpen, FlaskConical, Lock, Target, Unlock } from 'lucide-react';
import { Badge, Button, Card } from '../ui';
import { TierBadge } from './TierBadge';
import { skillLabel } from './tier';
import type { RoadmapItem } from '../../api/types';
import { colors } from '../../theme/tokens';

const TYPE_ICON = {
  course: BookOpen,
  lab: FlaskConical,
  scenario: Target,
} as const;

export interface RoadmapItemCardProps {
  item: RoadmapItem;
  /** When provided, renders an instructor grant control on gated items. */
  onGrant?: (item: RoadmapItem, reason: string) => void;
  /** True while a grant mutation for THIS item is in flight. */
  granting?: boolean;
}

/**
 * One recommended-content row on the roadmap. Above-tier (gated) items get a
 * Lock affordance and are visually muted; the server decides `unlocked`, the
 * UI only reflects it. Instructors optionally see a grant-access override.
 */
export function RoadmapItemCard({ item, onGrant, granting = false }: RoadmapItemCardProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const Icon = TYPE_ICON[item.contentType];
  const locked = !item.unlocked;

  return (
    <Card style={{ opacity: locked ? 0.7 : 1 }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1 }}>
          <Icon size={18} color={locked ? colors.hudAmber : colors.neon} />
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs">
              <Text fw={600} ff="JetBrains Mono" size="sm">
                {item.contentSlug}
              </Text>
              <TierBadge level={item.tierLevel} compact size="xs" />
              {item.isRequired ? <Badge variant="info" size="xs">required</Badge> : null}
              {item.isBuiltin ? <Badge variant="neutral" size="xs">builtin</Badge> : null}
            </Group>
            <Text size="xs" c="dimmed">
              {skillLabel(item.skillSlug)} · {item.contentType}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {locked ? (
            <Badge variant="warn" size="sm">
              <Group gap={4} wrap="nowrap">
                <Lock size={11} />
                <span>Locked</span>
              </Group>
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              <Group gap={4} wrap="nowrap">
                <Unlock size={11} />
                <span>Unlocked</span>
              </Group>
            </Badge>
          )}
        </Group>
      </Group>

      {locked && onGrant ? (
        <Stack gap="xs" mt="sm">
          {open ? (
            <>
              <Textarea
                size="xs"
                placeholder="Reason for the override (optional)"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                aria-label="Grant reason"
                autosize
                minRows={1}
              />
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="primary"
                  loading={granting}
                  onClick={() => onGrant(item, reason)}
                >
                  Confirm grant
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
              </Group>
            </>
          ) : (
            <Button
              size="xs"
              variant="secondary"
              leftIcon={<Unlock size={12} />}
              onClick={() => setOpen(true)}
            >
              Grant access
            </Button>
          )}
        </Stack>
      ) : null}
    </Card>
  );
}
